import { Component, Input, OnInit, AfterViewInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { Creativity } from '@adgate/modules/streaming/types/creativity.type';
import { StreamingService } from '@adgate/modules/streaming/services/streaming-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { StateService } from '@adgate/common/services/state-service';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { CreativityFilter } from '@adgate/modules/streaming/types/creativity-filter.type';
import { Status } from '@adgate/common/types/status.type';
import { UploadDialogComponent, UploadDialogResult } from '@adgate/common/components/upload-dialog-component/upload-dialog-component';
import { filter, finalize, switchMap, throwError } from 'rxjs';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { SpecCheckReportDialogComponent } from '@adgate/common/components/spec-check-report-dialog-component/spec-check-report-dialog-component';
import { environment } from '@env/environment';

@Component({
  selector: 'streaming-campaign-creativity-grid-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltip,
    ClipboardModule,
    MatRippleModule,
    MatSnackBarModule,
    ObjectDescPipe
  ],
  providers: [DatePipe],
  templateUrl: './streaming-campaign-creativity-grid-component.html',
  styleUrl: './streaming-campaign-creativity-grid-component.scss',
})
export class StreamingCampaignCreativityGridComponent implements OnInit, AfterViewInit
{
  private dialogService: DialogService = inject(DialogService);
  private streamingService: StreamingService = inject(StreamingService);
  private authService: AuthenticationService = inject(AuthenticationService);
  private stateService: StateService = inject(StateService);
  private _snackBar: MatSnackBar = inject(MatSnackBar);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Input() parentCampaign!: Campaign;

  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['refcode', 'format', 'placementName', 'status', 'created', 'actions'];
  dataSource: MatTableDataSource<Creativity>;
  filter: CreativityFilter = new CreativityFilter();

  showTable: boolean = false;
  allowEditHistory: boolean = false;
  allowEditCreativity: boolean = false;

  constructor()
  {
    this.dataSource = new MatTableDataSource<Creativity>([]);
  }

  ngOnInit(): void
  {
    this.initPrivileges();
    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
  }

  onRefresh()
  {
    if( this.parentCampaign != null ) {
      this.dialogService.showProgress();
      this.filter.orderId = this.parentCampaign.id;
      this.streamingService.getCreativities(this.filter).subscribe({
        next: (r: RESTApplicationResponse) => {
          this.dataSource.data = r.data;
          this.showTable = this.dataSource.data.length > 0;
          this.cdr.detectChanges();
          this.dialogService.hideProgress();
        },
        error: (err: Error) => {
          this.dialogService.openErrorDialog(err.message);
        }
      });
    }
  }

  onViewHistory(c: Creativity): void
  {
    this.dialogService.openHistoryDialog(
      Creativity.OBJECT_TYPE,
      c.id,
      `${c.brand} - ${c.placementName}`,
      c.status,
      this.allowEditHistory
    );
  }

  allowUploadCreativity(c: Creativity): boolean
  {
    return this.allowEditCreativity && (c.status.id == Status.STREAMING_CREATIVITY_CREATED || c.status.id == Status.STREAMING_CREATIVITY_SPECERROR || c.status.id == Status.STREAMING_CREATIVITY_REJECTED);
  }

  onUploadCreativity(c: Creativity): void
  {
    const dialogRef = this.dialogService.openAnyDialog(UploadDialogComponent, {
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result:UploadDialogResult | null) => result !== null),
      switchMap((result:UploadDialogResult) => {
        if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
        return this.streamingService.uploadCreativity_HTTP(c.id, result.media);
        } else if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
          this.dialogService.showProgress();
          return this.streamingService.uploadCreativity_ASPERA(c.id, result.media);
        } else {
          return throwError(() => new Error('Invalid upload method'));
        }
      }),
      finalize(() => {
        this.streamingService.getCreativities(this.filter).subscribe(r => {
          this.dataSource.data = r.data;
        });
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }
  
  onViewSpecCheckReport(c:Creativity)
  {
    const dialogRef = this.dialogService.openAnyDialog(SpecCheckReportDialogComponent,{
      data: {
        objectType: Creativity.OBJECT_TYPE,
        objectId: c.id,
        title: `${c.referenceCode}`,
        allowUpload: this.allowUploadCreativity(c),
        specsUrl: `${environment.helpCenterUrl}#specs-streaming`
      },
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result) => result != null)
    ).subscribe((result:'upload' | 'view-specs') => {
      if( result === 'upload' ) {
        this.onUploadCreativity(c);
      }
    });
  }

  allowDeleteCreativity(c: Creativity): boolean
  {
    return this.allowEditCreativity && (c.status.id == Status.STREAMING_CREATIVITY_CREATED || c.status.id == Status.STREAMING_CREATIVITY_SPECERROR);
  }

  onDeleteCreativity(c: Creativity): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete creativity "${c.referenceCode}"?`,
      {
        title: 'Delete Creativity',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        return this.streamingService.deleteCreativity(c.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onCopyRefCode(refCode: string)
  {
    this._snackBar.open(`${refCode} copied to clipboard`, '', { duration: 2000 });
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch (property) {
        case 'refcode': return item.referenceCode;
        case 'format': return item.type?.description;
        case 'placementName': return item.placementName;
        case 'status': return item.status.description;
        case 'created': return item.dateCreated;
        default: return item[property];
      }
    };
  }

  private initPrivileges(): void
  {
    let decodedToken = this.authService.decodeToken(this.stateService.data.userToken);
    this.allowEditHistory = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.STRM_EDITHISTORY]);
    this.allowEditCreativity = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.STRM_UPDATECREATIVITY]);
  }
}
